import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../api/client';
import {
  clearStoredAuthIfApiError,
  DIGIRASA_AUTH_UPDATED_EVENT,
  readStoredAuth,
} from '../Account/profileFormUtils';
import { fetchUserAddresses } from './addressesApi';
import { buildUnavailableLookup, isCartItemUnavailable } from './cartAvailabilityUtils';
import { fetchCheckCart } from './checkCartApi';
import {
  buildChargesFromCheckCart,
  calculateCouponDiscount,
  calculateGstAmount,
  CART_DEFAULT_GST_PERCENT,
  estimatePackagingFee,
  getBaseDeliveryCharge,
  resolveDeliveryCharge,
  resolvePackagingCharge,
} from './cartCouponUtils';
import { resolveInitialSelectedAddressId } from '../Checkout/checkoutAddressUtils';
import {
  distanceKmFromKitchen,
  getStoredSelectedAddressId,
  storeSelectedAddressId,
} from './cartDistanceUtils';

/**
 * @param {Array<{ id: number | null, latitude: number | null, longitude: number | null }>} list
 * @param {number | null | undefined} id
 */
function pickDeliverableAddress(list, id) {
  if (id == null) return null;
  const picked = list.find((a) => Number(a.id) === Number(id));
  if (
    !picked ||
    !Number.isFinite(picked.latitude) ||
    !Number.isFinite(picked.longitude)
  ) {
    return null;
  }
  return picked;
}

/**
 * @typedef {'idle' | 'loading' | 'ready' | 'guest' | 'error'} CartChargesStatus
 */

/**
 * @param {{
 *   cartItems: Array<{ id: number, price: number, quantity: number }>,
 *   cartTotal: number,
 *   fulfillmentType: string,
 *   appliedCoupon: import('./cartCouponUtils').NormalizedCoupon | null,
 *   distanceKm: number | null, // km for checkCart; null defers delivery/GST on delivery orders
 *   enabled?: boolean,
 * }} options
 */
export function useCartCharges({
  cartItems,
  cartTotal,
  fulfillmentType,
  appliedCoupon,
  distanceKm,
  enabled = true,
}) {
  const [status, setStatus] = useState(/** @type {CartChargesStatus} */ ('idle'));
  const [error, setError] = useState('');
  const [apiPackaging, setApiPackaging] = useState(/** @type {number | null} */ (null));
  const [apiDelivery, setApiDelivery] = useState(/** @type {number | null} */ (null));
  const [gstPercent, setGstPercent] = useState(CART_DEFAULT_GST_PERCENT);
  const [itemsAvailable, setItemsAvailable] = useState(true);
  const [unavailableItems, setUnavailableItems] = useState(/** @type {import('./cartAvailabilityUtils').UnavailableLineItem[]} */ ([]));

  const discount = useMemo(
    () => calculateCouponDiscount(appliedCoupon, cartTotal),
    [appliedCoupon, cartTotal],
  );

  const canUseDistance =
    fulfillmentType === 'pickup' || (distanceKm != null && Number.isFinite(distanceKm));

  const deferDeliveryAndTax =
    fulfillmentType === 'delivery' && !canUseDistance;

  const loadCharges = useCallback(async () => {
    if (!enabled || cartItems.length === 0 || cartTotal <= 0) {
      setStatus('idle');
      setApiPackaging(null);
      setApiDelivery(null);
      setError('');
      setUnavailableItems([]);
      return;
    }

    const { token } = readStoredAuth();
    if (!token) {
      setStatus('guest');
      setApiPackaging(null);
      setApiDelivery(null);
      setGstPercent(CART_DEFAULT_GST_PERCENT);
      setItemsAvailable(true);
      setUnavailableItems([]);
      setError('');
      return;
    }

    setStatus('loading');
    setError('');

    try {
      const hasDeliveryDistance =
        fulfillmentType === 'pickup' ||
        (distanceKm != null && Number.isFinite(distanceKm));
      const kmForApi = fulfillmentType === 'pickup' ? 0 : hasDeliveryDistance ? Number(distanceKm) : 0;
      const parsed = await fetchCheckCart({
        token,
        cartItems,
        distanceKm: kmForApi,
        fulfillmentType,
      });
      setApiPackaging(parsed.packagingCharges);
      setApiDelivery(hasDeliveryDistance ? parsed.deliveryCharges : null);
      setGstPercent(parsed.gstPercent);
      setItemsAvailable(parsed.isAvailable);
      setUnavailableItems(parsed.unavailableItems);
      setStatus('ready');
    } catch (err) {
      clearStoredAuthIfApiError(err);
      const { token: tokenAfter } = readStoredAuth();
      if (!tokenAfter) {
        setStatus('guest');
        setApiPackaging(null);
        setApiDelivery(null);
        setItemsAvailable(true);
        setUnavailableItems([]);
        setError('');
        return;
      }
      if (err instanceof ApiError && err.status === 401) {
        // Session-expired modal handles extend/logout; retry after auth update.
        setStatus('idle');
        setError('');
        return;
      }
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Could not calculate order charges.');
      setApiPackaging(null);
      setApiDelivery(null);
      setUnavailableItems([]);
    }
  }, [enabled, cartItems, cartTotal, fulfillmentType, distanceKm]);

  useEffect(() => {
    loadCharges();
  }, [loadCharges]);

  useEffect(() => {
    const onAuth = () => loadCharges();
    window.addEventListener(DIGIRASA_AUTH_UPDATED_EVENT, onAuth);
    return () => window.removeEventListener(DIGIRASA_AUTH_UPDATED_EVENT, onAuth);
  }, [loadCharges]);

  const unavailableLookup = useMemo(
    () => buildUnavailableLookup(unavailableItems),
    [unavailableItems],
  );

  const hasUnavailableItems = useMemo(() => {
    const inCart =
      status === 'ready' &&
      cartItems.some((item) => isCartItemUnavailable(item, unavailableLookup));
    return inCart || unavailableItems.length > 0;
  }, [status, cartItems, unavailableItems, unavailableLookup]);

  const charges = useMemo(() => {
    if (cartItems.length === 0) {
      return {
        packagingFee: 0,
        delivery: 0,
        gstTax: 0,
        gstPercent: CART_DEFAULT_GST_PERCENT,
        baseDelivery: 0,
        source: 'empty',
      };
    }

    if (status === 'guest' || status === 'error') {
      const packagingRaw =
        status === 'guest' ? estimatePackagingFee(cartTotal) : estimatePackagingFee(cartTotal);
      const packagingFee = resolvePackagingCharge(packagingRaw, appliedCoupon);
      const baseDelivery =
        fulfillmentType === 'pickup'
          ? 0
          : deferDeliveryAndTax
            ? 0
            : getBaseDeliveryCharge(cartTotal);
      const delivery = resolveDeliveryCharge({
        baseDelivery,
        fulfillmentType,
        appliedCoupon,
      });
      const gstTax = deferDeliveryAndTax
        ? 0
        : calculateGstAmount(CART_DEFAULT_GST_PERCENT, cartTotal, packagingFee, discount);
      return {
        packagingFee,
        delivery,
        gstTax,
        gstPercent: CART_DEFAULT_GST_PERCENT,
        baseDelivery,
        source: status === 'guest' ? 'estimate' : 'estimate-fallback',
      };
    }

    if (status !== 'ready' || apiPackaging == null) {
      const packagingFee = resolvePackagingCharge(
        estimatePackagingFee(cartTotal),
        appliedCoupon,
      );
      return {
        packagingFee,
        delivery: 0,
        gstTax: 0,
        gstPercent: CART_DEFAULT_GST_PERCENT,
        baseDelivery: 0,
        source: 'loading',
      };
    }

    if (deferDeliveryAndTax) {
      const packagingFee = resolvePackagingCharge(apiPackaging, appliedCoupon);
      return {
        packagingFee,
        delivery: 0,
        gstTax: 0,
        gstPercent,
        baseDelivery: 0,
        source: 'checkCart-packaging',
      };
    }

    if (apiDelivery == null) {
      const packagingFee = resolvePackagingCharge(apiPackaging, appliedCoupon);
      return {
        packagingFee,
        delivery: 0,
        gstTax: 0,
        gstPercent,
        baseDelivery: 0,
        source: 'checkCart-packaging',
      };
    }

    return {
      ...buildChargesFromCheckCart({
        packagingCharges: apiPackaging,
        deliveryCharges: apiDelivery,
        gstPercent,
        cartTotal,
        discount,
        fulfillmentType,
        appliedCoupon,
      }),
      source: 'checkCart',
    };
  }, [
    status,
    apiPackaging,
    apiDelivery,
    gstPercent,
    cartItems.length,
    cartTotal,
    discount,
    fulfillmentType,
    appliedCoupon,
    deferDeliveryAndTax,
  ]);

  return {
    status,
    error,
    discount,
    deferDeliveryAndTax,
    itemsAvailable,
    hasUnavailableItems,
    unavailableItems,
    isCartItemUnavailable: (item) => isCartItemUnavailable(item, unavailableLookup),
    packagingFee: charges.packagingFee,
    delivery: charges.delivery,
    gstTax: charges.gstTax,
    gstPercent: charges.gstPercent,
    baseDelivery: charges.baseDelivery,
    chargeSource: charges.source,
    reload: loadCharges,
  };
}

/**
 * Distance for cart pricing — only explicitly selected delivery address (localStorage).
 * @param {{ enabled?: boolean }} [options]
 */
export function useCartDeliveryDistance(options = {}) {
  const { enabled = true } = options;
  const [distanceKm, setDistanceKm] = useState(/** @type {number | null} */ (null));
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [selectedId, setSelectedId] = useState(/** @type {number | null} */ (null));
  const [addresses, setAddresses] = useState(/** @type {import('./addressesApi').ReturnType<typeof normalizeUserAddress>[]} */ ([]));
  const [status, setStatus] = useState(/** @type {'idle' | 'loading' | 'ready' | 'guest'} */ ('idle'));

  const applySelection = useCallback((list, id) => {
    const picked = pickDeliverableAddress(list, id);
    setSelectedId(id != null ? Number(id) : null);
    setSelectedAddress(picked);
    setDistanceKm(picked ? distanceKmFromKitchen(picked) : null);
  }, []);

  const load = useCallback(async () => {
    if (!enabled) {
      setDistanceKm(null);
      setSelectedAddress(null);
      setSelectedId(null);
      setAddresses([]);
      setStatus('idle');
      return;
    }
    const { token } = readStoredAuth();
    if (!token) {
      setStatus('guest');
      setDistanceKm(null);
      setSelectedAddress(null);
      setSelectedId(null);
      setAddresses([]);
      return;
    }
    setStatus('loading');
    try {
      const list = await fetchUserAddresses(token);
      setAddresses(list);

      let effectiveId = getStoredSelectedAddressId();
      let picked = pickDeliverableAddress(list, effectiveId);
      if (!picked) {
        const resolved = resolveInitialSelectedAddressId(list);
        if (resolved != null) {
          effectiveId = resolved;
          storeSelectedAddressId(resolved);
          picked = pickDeliverableAddress(list, resolved);
        }
      }
      applySelection(list, picked?.id ?? effectiveId);
      setStatus('ready');
    } catch (err) {
      clearStoredAuthIfApiError(err);
      const { token: tokenAfter } = readStoredAuth();
      if (!tokenAfter) {
        setStatus('guest');
        setDistanceKm(null);
        setSelectedAddress(null);
        setSelectedId(null);
        setAddresses([]);
        return;
      }
      setAddresses([]);
      setDistanceKm(null);
      setSelectedAddress(null);
      setSelectedId(null);
      setStatus('ready');
    }
  }, [enabled, applySelection]);

  const selectAddress = useCallback(
    (id) => {
      storeSelectedAddressId(id);
      applySelection(addresses, id);
    },
    [addresses, applySelection],
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onAuth = () => load();
    window.addEventListener(DIGIRASA_AUTH_UPDATED_EVENT, onAuth);
    return () => window.removeEventListener(DIGIRASA_AUTH_UPDATED_EVENT, onAuth);
  }, [load]);

  return {
    distanceKm,
    selectedAddress,
    selectedId,
    addresses,
    savedAddressCount: addresses.length,
    status,
    selectAddress,
    reload: load,
  };
}
